<script setup>
import { ref, computed } from 'vue'
import StatusPill from '@/components/shell/StatusPill.vue'
import DetailDrawer from '@/components/shell/DetailDrawer.vue'
import { store } from '@/store/appStore.js'
import { STAGES } from '@/data/shared/orderModel.js'

const search = ref(''); const statusFilter = ref('All')
const drawerOrderNo = ref(null)
const lrNumber = ref('')
const allocQty = ref({})

const orders = computed(() => store.warehouseV2.orders.value)
const inventory = store.warehouseV2.inventory
const stockBySku = computed(() => Object.fromEntries(inventory.map(i => [i.productSku, i.availableQty])))
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return orders.value.filter(o => {
    const matchesQ = !q || o.orderNo.toLowerCase().includes(q) || o.pharmacyName.toLowerCase().includes(q)
    const matchesStatus = statusFilter.value === 'All' || o.currentStage === statusFilter.value
    return matchesQ && matchesStatus
  }).sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate))
})
const statusOptions = computed(() => ['All', ...new Set(orders.value.map(o => o.currentStage))])
const drawerOrder = computed(() => drawerOrderNo.value ? orders.value.find(o => o.orderNo === drawerOrderNo.value) || null : null)
const stageIdx = computed(() => drawerOrder.value ? STAGES.indexOf(drawerOrder.value.currentStage) : -1)
const isLateral = computed(() => drawerOrder.value && !STAGES.includes(drawerOrder.value.currentStage))

function openDrawer(o) {
  drawerOrderNo.value = o.orderNo
  lrNumber.value = ''
  const seed = {}
  o.lineItems.filter(li => !li.allocated).forEach(li => {
    seed[li.productSku] = li.fulfilledQty !== null && li.fulfilledQty !== undefined ? li.fulfilledQty : li.quantity
  })
  allocQty.value = seed
}
function quickDispatch(o) { store.warehouseV2.dispatchOrder(o.orderNo, o.lrNumber) }
function allocateAll() {
  if (!drawerOrderNo.value) return
  store.warehouseV2.allocateStock(drawerOrderNo.value, { ...allocQty.value })
  allocQty.value = drawerOrder.value
    ? Object.fromEntries(drawerOrder.value.lineItems.filter(li => !li.allocated).map(li => [li.productSku, li.fulfilledQty ?? li.quantity]))
    : {}
}
function allocateLine(li) {
  if (!drawerOrderNo.value) return
  store.warehouseV2.allocateStock(drawerOrderNo.value, { [li.productSku]: allocQty.value[li.productSku] })
}
function dispatch() {
  if (!drawerOrderNo.value) return
  store.warehouseV2.dispatchOrder(drawerOrderNo.value, lrNumber.value.trim())
  drawerOrderNo.value = null
}
</script>

<template>
  <div>
    <div class="card module-header"><h2>{{ orders.length }} orders</h2></div>

    <div class="card">
      <div class="toolbar">
        <input class="search-input" v-model="search" placeholder="Search order no. or pharmacy…" />
        <select class="filter-select" v-model="statusFilter"><option v-for="s in statusOptions" :key="s">{{ s }}</option></select>
        <span class="result-count">{{ filtered.length }} of {{ orders.length }} shown</span>
      </div>
      <div class="table-wrap">
        <table class="data-table">
          <thead><tr><th>Order No.</th><th>Pharmacy</th><th>Products</th><th>Value</th><th>Status</th><th>SLA</th><th></th></tr></thead>
          <tbody>
            <tr v-for="o in filtered" :key="o.orderNo" class="clickable" @click="openDrawer(o)">
              <td class="cell-strong">{{ o.orderNo }}</td><td>{{ o.pharmacyName }}</td>
              <td>{{ o.lineItems[0].productName }}<span v-if="o.lineItems.length>1" class="cell-muted"> +{{ o.lineItems.length-1 }} more</span></td>
              <td class="cell-muted">${{ o.orderValue.toLocaleString() }}</td>
              <td><StatusPill :status="o.currentStage" :label="o.currentStage" /></td>
              <td><StatusPill v-if="STAGES.includes(o.currentStage)" :status="o.trafficLight" dot-only /></td>
              <td>
                <button v-if="o.currentStage==='Dispatch Pending'" class="filter-select" style="cursor:pointer;font-size:11px;" @click.stop="quickDispatch(o)">Dispatch</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <DetailDrawer :open="!!drawerOrder" :title="drawerOrder?.orderNo || ''" wide @close="drawerOrderNo=null">
      <template v-if="drawerOrder">
        <div class="oos-meta-grid" style="margin-bottom:18px;">
          <div><div class="label">Pharmacy</div>{{ drawerOrder.pharmacyName }}</div>
          <div><div class="label">Order Date</div>{{ drawerOrder.orderDate }}</div>
          <div><div class="label">Value</div>${{ drawerOrder.orderValue.toLocaleString() }}</div>
        </div>

        <table class="data-table" style="margin-bottom:18px;">
          <thead><tr><th>Product</th><th>Ordered</th><th>Telecaller Approved</th><th>Current Stock</th><th>Free</th><th>Total Delivered</th><th>Line Value</th><th>Allocated</th></tr></thead>
          <tbody>
            <tr v-for="li in drawerOrder.lineItems" :key="li.productSku">
              <td class="cell-strong">{{ li.productName }} <span v-if="li.schemeApplied" class="tier-badge Gold">10+1</span></td>
              <td class="cell-muted">{{ li.quantity }}</td>
              <td class="cell-muted">{{ li.fulfilledQty ?? '—' }}</td>
              <td :style="(stockBySku[li.productSku] ?? 0) < li.quantity ? 'color:var(--status-red);font-weight:700;' : ''">{{ stockBySku[li.productSku] ?? '—' }}</td>
              <td class="cell-muted">{{ li.freeQuantity || 0 }}</td>
              <td class="cell-strong">{{ li.totalQuantity || li.quantity }}</td><td class="cell-muted">${{ li.lineValue.toLocaleString() }}</td>
              <td><StatusPill :status="li.allocated ? 'green' : 'yellow'" :label="li.allocated ? (li.warehouseFulfilmentStatus || 'Allocated') : 'Pending'" /></td>
            </tr>
          </tbody>
        </table>

        <div v-if="!isLateral" class="eyebrow" style="margin-bottom:10px;">Status Timeline</div>
        <div v-if="!isLateral" class="stepper" style="margin-bottom:18px;">
          <div v-for="(s,i) in STAGES" :key="s" class="stepper-step">
            <div class="stepper-marker">
              <div class="stepper-dot" :class="{ done: i<stageIdx, current: i===stageIdx }"></div>
              <div v-if="i<STAGES.length-1" class="stepper-line" :class="{ done: i<stageIdx }"></div>
            </div>
            <div class="stepper-label" :class="i===stageIdx?'current':(i<stageIdx?'':'pending')">
              {{ s }}
              <div v-if="drawerOrder.statusHistory[i]" class="cell-muted" style="font-size:10.5px;">{{ new Date(drawerOrder.statusHistory[i].timestamp).toLocaleString() }}</div>
            </div>
          </div>
        </div>
        <div v-if="drawerOrder.currentStage==='Cancelled'" class="oos-remarks" style="margin-bottom:16px;">{{ drawerOrder.cancelReason || '—' }}</div>
        <div v-if="drawerOrder.lrNumber" class="oos-remarks" style="margin-bottom:16px;">
          <strong style="display:block;font-size:11px;text-transform:uppercase;color:var(--color-text-muted);">LR Number</strong>{{ drawerOrder.lrNumber }}
        </div>

        <!-- Allocate Stock: independent quantity per line, up or down from
             whatever Telecaller fulfilled — their number stays visible for
             reference but doesn't cap Warehouse's own decision. -->
        <div v-if="drawerOrder.currentStage==='Stock Allocation'" style="border-top:1px solid var(--color-border);padding-top:16px;margin-bottom:16px;">
          <div class="eyebrow" style="margin-bottom:10px;">Allocate Stock</div>
          <div v-for="li in drawerOrder.lineItems.filter(x=>!x.allocated)" :key="li.productSku" style="display:flex;align-items:center;gap:10px;margin-bottom:10px;">
            <div style="flex:1;">
              <div class="cell-strong" style="font-size:13px;">{{ li.productName }}</div>
              <div class="cell-muted" style="font-size:11.5px;">Ordered {{ li.quantity }} · Telecaller approved {{ li.fulfilledQty ?? '—' }}</div>
            </div>
            <input class="form-input" type="number" min="0" :max="li.quantity" style="width:90px;" v-model.number="allocQty[li.productSku]" />
            <button class="filter-select" style="cursor:pointer;font-size:11px;" @click="allocateLine(li)">Allocate</button>
          </div>
          <button class="btn-primary" style="margin-top:8px;" @click="allocateAll">Allocate All Entered Quantities</button>
        </div>

        <!-- Dispatch: only for Dispatch Pending -->
        <div v-if="drawerOrder.currentStage==='Dispatch Pending'" style="border-top:1px solid var(--color-border);padding-top:16px;">
          <div class="eyebrow" style="margin-bottom:10px;">Dispatch</div>
          <input class="form-input" v-model="lrNumber" placeholder="LR Number" style="margin-bottom:10px;" />
          <button class="btn-primary" @click="dispatch">Dispatch</button>
        </div>
      </template>
    </DetailDrawer>
  </div>
</template>
